import React, { useCallback, useEffect, useState, useRef, useContext } from 'react';
import { ChevronRightIcon, ChevronLeftIcon, ChevronRightIcon as ArrowRightIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { SignInModalContext } from '../App';
export const HeroSection = () => {
  const navigate = useNavigate();
  const {
    openTeacherLoginModal
  } = useContext(SignInModalContext);
  const [isVisible, setIsVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAnimatedText, setShowAnimatedText] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // References to the navigation buttons
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const backgroundImages = ['https://uploadthingy.s3.us-west-1.amazonaws.com/1YYDABxxgC3iLBdkcPAnB8/Pianist.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/4EgoJvcEaWNNQgd41tKmBa/Young_boy_teaching_to_play_guitar_stock_photo.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/u3uxe3Cx1duCHvK2vyX2kQ/Kids_Practice.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/aattiLkEeShSh5MAL8Snpt/Violin_Practice.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/efvaWCRmfP55oUjAv1D2CU/Music_production.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/wsCCvA1A9jzHPFy6HSCTYQ/Singer.jpg'];
  // Detect mobile on mount
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  // Function to handle text animation reset
  const resetTextAnimation = useCallback(() => {
    setShowAnimatedText(false);
    // Use a shorter animation delay for mobile, increased by 20%
    const delay = isMobile ? 600 : 960; // Increased from 500/800
    setTimeout(() => {
      setShowAnimatedText(true);
    }, delay);
  }, [isMobile]);
  // Function to change slides directly
  const changeSlide = useCallback(direction => {
    // Update the current image index
    setCurrentImageIndex(prevIndex => {
      if (direction === 'prev') {
        return prevIndex === 0 ? backgroundImages.length - 1 : prevIndex - 1;
      } else {
        return prevIndex === backgroundImages.length - 1 ? 0 : prevIndex + 1;
      }
    });
    // Reset text animation with the new slide
    setTimeout(() => {
      resetTextAnimation();
    }, 50);
  }, [backgroundImages.length, resetTextAnimation]);
  // Function to navigate to the previous slide
  const goToPreviousSlide = useCallback(e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Prevent navigation if a transition is already in progress
    if (isTransitioning) return;
    // Set transitioning state to prevent multiple taps/clicks
    setIsTransitioning(true);
    // Change the slide
    changeSlide('prev');
    // Reset transitioning state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, isMobile ? 600 : 960);
  }, [isTransitioning, isMobile, changeSlide]);
  // Function to navigate to the next slide
  const goToNextSlide = useCallback(e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Prevent navigation if a transition is already in progress
    if (isTransitioning) return;
    // Set transitioning state to prevent multiple taps/clicks
    setIsTransitioning(true);
    // Change the slide
    changeSlide('next');
    // Reset transitioning state after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, isMobile ? 600 : 960);
  }, [isTransitioning, isMobile, changeSlide]);
  // Set up direct DOM event listeners for mobile touch events
  useEffect(() => {
    // Only set up these listeners on mobile
    if (!isMobile) return;
    const prevButton = prevButtonRef.current;
    const nextButton = nextButtonRef.current;
    if (!prevButton || !nextButton) return;
    // Function to handle touch events for previous button
    const handlePrevTouch = e => {
      e.preventDefault();
      e.stopPropagation();
      // Add temporary active class for visual feedback
      prevButton.querySelector('div').classList.add('bg-white/40');
      // Only proceed if not already transitioning
      if (!isTransitioning) {
        goToPreviousSlide();
      }
      // Remove active class after a short delay
      setTimeout(() => {
        if (prevButton && prevButton.querySelector('div')) {
          prevButton.querySelector('div').classList.remove('bg-white/40');
        }
      }, 150);
      return false;
    };
    // Function to handle touch events for next button
    const handleNextTouch = e => {
      e.preventDefault();
      e.stopPropagation();
      // Add temporary active class for visual feedback
      nextButton.querySelector('div').classList.add('bg-white/40');
      // Only proceed if not already transitioning
      if (!isTransitioning) {
        goToNextSlide();
      }
      // Remove active class after a short delay
      setTimeout(() => {
        if (nextButton && nextButton.querySelector('div')) {
          nextButton.querySelector('div').classList.remove('bg-white/40');
        }
      }, 150);
      return false;
    };
    // Add event listeners with the non-passive option
    prevButton.addEventListener('touchend', handlePrevTouch, {
      passive: false
    });
    nextButton.addEventListener('touchend', handleNextTouch, {
      passive: false
    });
    // Clean up event listeners on unmount
    return () => {
      if (prevButton) {
        prevButton.removeEventListener('touchend', handlePrevTouch);
      }
      if (nextButton) {
        nextButton.removeEventListener('touchend', handleNextTouch);
      }
    };
  }, [isMobile, isTransitioning, goToPreviousSlide, goToNextSlide]);
  useEffect(() => {
    setIsVisible(true);
    // Trigger the animated text after a delay when the component mounts
    setTimeout(() => {
      setShowAnimatedText(true);
    }, isMobile ? 600 : 960);
    // Set up image rotation interval
    const rotationInterval = setInterval(() => {
      // Only advance to next slide if no manual transition is in progress
      if (!isTransitioning) {
        goToNextSlide();
      }
    }, 7200); // Increased from 6000
    // Clean up interval on component unmount
    return () => clearInterval(rotationInterval);
  }, [isTransitioning, goToNextSlide, isMobile]);
  const handleOpenSignInModal = () => {
    openTeacherLoginModal();
  };
  const handleFindTeacherClick = () => {
    // Ensure we scroll to top when navigating
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };
  // Calculate transition duration based on device
  const transitionDuration = isMobile ? 'duration-600' : 'duration-960';
  // Handle click events for both desktop and mobile
  const handleClick = direction => e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Execute slide change for both desktop and mobile
    if (direction === 'prev') {
      goToPreviousSlide(e);
    } else {
      goToNextSlide(e);
    }
  };
  return <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-16" data-id="element-1497">
      <div className="absolute inset-0 z-0" data-id="element-1498">
        {/* Background image with overlay */}
        <div className="absolute inset-0 bg-navy-900/40 z-10" data-id="element-1499"></div>
        {/* Carousel images with transition */}
        {backgroundImages.map((image, index) => <div key={index} className={`absolute inset-0 transition-opacity ${transitionDuration} ease-in-out ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`} style={{
        backgroundImage: `url('${image}')`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.15)'
      }} data-id="element-1500"></div>)}
        {/* Carousel navigation arrows */}
        <div className="absolute inset-0 hidden md:flex items-center justify-between z-20 px-4 sm:px-6 md:px-8" data-id="element-1501">
          {/* Left arrow button */}
          <button ref={prevButtonRef} className="w-16 h-16 flex items-center justify-center pointer-events-auto" onClick={handleClick('prev')} aria-label="Previous slide" style={{
          WebkitTapHighlightColor: 'transparent'
        }} data-id="element-1502">
            <div className={`${isTransitioning ? 'bg-white/10' : 'bg-white/20 hover:bg-white/30'} text-white rounded-full p-3 backdrop-blur-sm transition-colors`} data-id="element-1503">
              <ChevronLeftIcon className="h-6 w-6 sm:h-8 sm:w-8" data-id="element-1504" />
            </div>
          </button>
          {/* Right arrow button */}
          <button ref={nextButtonRef} className="w-16 h-16 flex items-center justify-center pointer-events-auto" onClick={handleClick('next')} aria-label="Next slide" style={{
          WebkitTapHighlightColor: 'transparent'
        }} data-id="element-1505">
            <div className={`${isTransitioning ? 'bg-white/10' : 'bg-white/20 hover:bg-white/30'} text-white rounded-full p-3 backdrop-blur-sm transition-colors`} data-id="element-1506">
              <ArrowRightIcon className="h-6 w-6 sm:h-8 sm:w-8" data-id="element-1507" />
            </div>
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10" data-id="element-1508">
        <div className="max-w-2xl" data-id="element-1509">
          {/* Animated text for first slide */}
          {currentImageIndex === 0 && <div className={`transition-all ${isMobile ? 'duration-600' : 'duration-960'} ease-out overflow-hidden mb-4 ${showAnimatedText ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`} data-id="element-1510">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-extralight italic text-white/95 tracking-wide" data-id="element-1511">
                From Teaching to Thriving — Built for Music Educators
              </h2>
            </div>}
          {/* Animated text for guitar lesson slide (index 1) */}
          {currentImageIndex === 1 && <div className={`transition-all ${isMobile ? 'duration-600' : 'duration-960'} ease-out overflow-hidden mb-4 ${showAnimatedText ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`} data-id="element-1512">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-extralight italic text-white/95 tracking-wide" data-id="element-1513">
                Tailored Guitar Lessons For All Ages.
              </h2>
            </div>}
          {/* Animated text for music school slide (index 2) */}
          {currentImageIndex === 2 && <div className={`transition-all ${isMobile ? 'duration-600' : 'duration-960'} ease-out overflow-hidden mb-4 ${showAnimatedText ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`} data-id="element-1514">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-extralight italic text-white/95 tracking-wide" data-id="element-1515">
                Discover Music Schools In Your Area.
              </h2>
            </div>}
          {/* Animated text for violin lesson slide (index 3) */}
          {currentImageIndex === 3 && <div className={`transition-all ${isMobile ? 'duration-600' : 'duration-960'} ease-out overflow-hidden mb-4 ${showAnimatedText ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`} data-id="element-1516">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-extralight italic text-white/95 tracking-wide" data-id="element-1517">
                Violin Lessons Designed For Every Skill Level.
              </h2>
            </div>}
          {/* Animated text for music production slide (index 4) */}
          {currentImageIndex === 4 && <div className={`transition-all ${isMobile ? 'duration-600' : 'duration-960'} ease-out overflow-hidden mb-4 ${showAnimatedText ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`} data-id="element-1518">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-extralight italic text-white/95 tracking-wide" data-id="element-1519">
                Explore The World Of Modern Music Production.
              </h2>
            </div>}
          {/* Animated text for vocal recording studio slide (index 5) */}
          {currentImageIndex === 5 && <div className={`transition-all ${isMobile ? 'duration-600' : 'duration-960'} ease-out overflow-hidden mb-4 ${showAnimatedText ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`} data-id="element-1520">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-extralight italic text-white/95 tracking-wide" data-id="element-1521">
                Sing Like A Pro. Start Your Journey Today.
              </h2>
            </div>}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-1522">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-6" data-id="element-1523">
              Professional Tools for
              <br data-id="element-1524" />
              <span className="text-deep-pink" data-id="element-1525">Music Teachers</span>
            </h1>
            <p className="text-xl text-white mb-8" data-id="element-1526">
              Manage lessons, students, scheduling, and growth — all in one
              place.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-1527">
              <Link to="/teacher-signup" className="bg-deep-pink hover:bg-burgundy text-white px-6 py-3 rounded-lg font-medium flex items-center transition-colors sm:bg-deep-pink md:bg-deep-pink" onClick={() => {
              // Ensure we scroll to top when navigating
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 100);
            }} data-id="element-1528">
                Become a Teacher
                <ChevronRightIcon className="ml-2 h-5 w-5" data-id="element-1529" />
              </Link>
              {/* UI GATING: Student entry point hidden for teacher-only beta */}
              {false && <Link to="/student-signup" className="bg-deep-pink hover:bg-burgundy text-white px-6 py-3 rounded-lg font-medium flex items-center transition-colors sm:bg-deep-pink md:bg-deep-pink" onClick={handleFindTeacherClick} data-id="element-1530">
                  Find a Teacher
                  <ChevronRightIcon className="ml-2 h-5 w-5" data-id="element-1531" />
                </Link>}
              <button className="bg-transparent hover:bg-white/10 text-white border border-white px-6 py-3 rounded-lg font-medium transition-colors" onClick={handleOpenSignInModal} data-id="element-1532">
                Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Add custom CSS for responsive background sizing */}
      <style jsx data-id="element-1533">{`
        @media (max-width: 768px) {
          button.bg-deep-pink:hover {
            background-color: #8b1e3f !important;
          }
        }
      `}</style>
    </section>;
};